import { createStyles, makeStyles, Typography } from '@material-ui/core';
import cn from 'classnames';
import { FormikProps } from 'formik';
import * as React from 'react';
import { TranquiTheme } from '../../containers/App/theme';
import { useSpace } from '../../content/utils/useSpace';
import { useBankContext } from '../../dictionaries/context/BankContext';
import { DictionaryContextType } from '../../dictionaries/context/DictionaryContextTypes';
import useDictionary from '../../dictionaries/context/useDictionary';
import MarkdownRender from '../../dictionaries/MarkdownRender';
import RenderText from '../../dictionaries/RenderText';
import { BankType, BrandType, NegotiationOption, NegotiationOptionAlternative, Normative026, OutDebt } from '../../generated/graphql';
import { getNegBooleans } from '../../utils/getNegBooleans';
import { getNegotiationOptionLabel } from '../../utils/getNegotiationOptionLabel';
import thousandSeparator from '../../utils/thousandSeparator';
import { useGetProductNumberDef } from '../../utils/useGetProductNumberDef';
import FormikCheck from '../FormikInputWrappers/FormikCheck';
import AlertBase from './AlertBase';
import { useGetMaxPaymentDate } from './useGetMaxPaymentDate';

interface UserConfirmationObligationAlertProps {
  negotiation?: NegotiationOption;
  alternative: NegotiationOptionAlternative;
  formik?: FormikProps<any>;
  outDebt: OutDebt;
  outDebts?: OutDebt[];
}

const UserConfirmationObligationAlert: React.FC<UserConfirmationObligationAlertProps> = (props) => {
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  const classes = useStyles();
  const { gT } = useDictionary();
  const { negotiation, formik, outDebt, alternative, outDebts } = props;
  const { productNumber, extra } = outDebt;

  const {
    isBalanceBehindPayment,
    isScotiabank,
    isRefinance,
    isBogota,
    isTranqui,
  } = getNegBooleans(outDebt, negotiation);

  const productNumberDef = useGetProductNumberDef(outDebt);

  const [ bank ] = useBankContext();
  const negPaymentDate = useGetMaxPaymentDate(
    { option: negotiation, outDebt, useMaxPaymentDateOverride: true },
  );

  const outDebtHeadProductNumber = outDebts?.find((od) => (
    isBogota && isTranqui
    && od.extra?.headProductNumber
    && od.productNumber
  ))?.productNumber || '';

  const negType = negotiation && getNegotiationOptionLabel(
    negotiation.type,
    bank as BankType,
    extra?.normative026 as Normative026,
    alternative,
    outDebt.brand,
  );

  const context: DictionaryContextType = {
    productNumber: productNumber || '',
    ...extra?.popupRate && { popupRate: extra.popupRate },
    ...negotiation && {
      negType,
      negTypeLabel: negType,
    },
    monthlyPayment: thousandSeparator(alternative?.value || 0),
    bank: bank as BankType,
    negPaymentDate,
    headProductNumber: outDebtHeadProductNumber,
    alternative,
    productNumberDef,
  };

  // space implementation
  const alertConditionsSpace = useSpace('NEG_OPTION_SUMMARY', 'alert.conditions', {
    ...context,
    outDebt,
    option: negotiation,
    alternative,
  });

  if (!negotiation || !outDebt) return null;

  const normative026Subkey = (isScotiabank && isRefinance && extra?.normative026) ? `_${extra?.normative026}` : '';
  const blockTypeSubkey = (isScotiabank && isRefinance && extra?.blockType) ? `_${extra?.blockType}` : '';
  const c25Subkey = (isScotiabank && isRefinance && outDebt?.extra?.isC25) ? '_C25' : '';
  const contentSubKey = (() => {
    if (!isBogota) return negotiation.type;
    if (isBalanceBehindPayment) return alternative?.type;
    if (isRefinance) return `${negotiation.type}_${alternative?.type}`;
    return negotiation.type;
  })();
  const textKey = `alert_acceptConditions_content_${contentSubKey}`;
  const text = gT(textKey, context);
  const termsAndConditions = gT(`termsAndConditions_text${normative026Subkey}${blockTypeSubkey}${c25Subkey}`);
  const completeKey = [ text, termsAndConditions ];

  const isMultiDebt = outDebts && outDebts.filter((od) => (
    od.bank === BankType.BancoDeBogota && od.brand === BrandType.Tranquiapp
  )).length > 1;

  if (isMultiDebt
    && outDebtHeadProductNumber
    && !outDebt?.productNumber?.includes(outDebtHeadProductNumber)) {
    completeKey.push(gT('alert_multidebt_text',
      { headProductNumber: outDebtHeadProductNumber }));
  }

  return (
    <AlertBase
      type={formik?.values.acceptCheckbox ? 'rectictionsAccepted' : 'restrictions'}
      className={cn(classes.root, 'mb-2')}
      title="Debes tener en cuenta"
    >
      <MarkdownRender text={alertConditionsSpace.text || completeKey.join('\n&nbsp;  \n&nbsp;  \n')} />
      {!!formik
        && (
        <FormikCheck
          className="mb-n2"
          formik={formik}
          valueId="acceptCheckbox"
          iconClassName={classes.iconCheckBox}
          helperTextClassName="mb-2"
          labelCheck={(
            <Typography className={classes.checkBoxText}>
              <RenderText textKey="alert_acceptConditions_formik" />
            </Typography>
          )}
        />
        )}
    </AlertBase>
  );
};

const useStyles = makeStyles((theme: TranquiTheme) => createStyles({
  root: {
    '& a': {
      overflowWrap: 'anywhere',
    },
  },
  checkBox: {
    padding: theme.spacing(-1, 0),
  },
  checkBoxText: {
    ...theme.typography.body2,
  },
  iconCheckBox: {
    width: '0.8em',
    height: '0.8em',
  },
}));

export default UserConfirmationObligationAlert;
